/**
 * Content (MAR-003, REQ-016 AC-010).
 *
 * Every blog entry, and the one action that matters: a draft becomes a
 * published post only when a named person approves it. Before this screen
 * existed the pipeline wrote straight to `published`, so every post went live
 * without anyone having read it.
 *
 * The screen is built to make reading the post the easy path and approving it
 * the deliberate one. The body is shown in full, not summarised — an approval
 * given from an excerpt is the thing the EU AI Act's Article 50 duty is about,
 * and a UI that lets you approve from a list teaches you to approve from a
 * list.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  ExternalLink,
  EyeOff,
  FileText,
  Loader2,
  MailWarning,
  Sparkles,
  XCircle,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useLocaleFormat } from "@/hooks/useLocaleFormat";
import apiClient from "@/services/api/apiClient";
import { sanitizeArticleHtml } from "@/utils/sanitizeArticleHtml";

type ReviewState = "draft" | "approved" | "rejected" | "legacy";

/**
 * What the machine already measured, so the reviewer spends their time on what
 * it cannot: whether the article is true and worth reading. A `fail` blocks
 * approval server-side; a `warn` is a judgement the reviewer makes.
 */
interface CheckResult {
  id: string;
  severity: "pass" | "warn" | "fail";
  detail: string;
}

const CHECK_STYLE: Record<CheckResult["severity"], string> = {
  pass: "bg-emerald-50 text-emerald-800 ring-emerald-600/20",
  warn: "bg-amber-50 text-amber-800 ring-amber-600/20",
  fail: "bg-red-50 text-red-800 ring-red-600/20",
};

interface ContentItem {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  category: string;
  tags: string[];
  author: string;
  language: string;
  primaryKeyword: string | null;
  aiGenerated: boolean;
  published: boolean;
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
  readTimeMin: number | null;
  metaTitle: string | null;
  metaDesc: string | null;
  imageUrl: string | null;
  imageAlt: string | null;
  checks: CheckResult[] | null;
  checkedAt: string | null;
  reviewState: ReviewState;
  reviewedBy: string | null;
  reviewedAt: string | null;
  reviewNote: string | null;
}

interface Revision {
  id: string;
  slug: string;
  title: string;
  reason: string;
  checks: CheckResult[] | null;
  createdAt: string;
}

interface RefreshCandidate {
  slug: string;
  title: string;
  reason: string;
  trigger: "advisory" | "score" | "age";
  ageDays: number;
  score: number | null;
  readers: number;
}

interface ListResponse {
  items: ContentItem[];
  counts: Partial<Record<ReviewState, number>>;
}

const STATE_STYLE: Record<ReviewState, string> = {
  draft: "bg-amber-50 text-amber-700 ring-amber-600/20",
  approved: "bg-emerald-50 text-emerald-700 ring-emerald-600/20",
  rejected: "bg-red-50 text-red-700 ring-red-600/20",
  legacy: "bg-gray-50 text-gray-600 ring-gray-500/20",
};

const STATE_LABEL: Record<ReviewState, string> = {
  draft: "Needs review",
  approved: "Reviewed",
  rejected: "Rejected",
  legacy: "Published unreviewed",
};

const FILTERS: Array<{ id: ReviewState | "all"; label: string }> = [
  { id: "all", label: "All" },
  { id: "draft", label: "Needs review" },
  { id: "approved", label: "Reviewed" },
  { id: "legacy", label: "Unreviewed" },
  { id: "rejected", label: "Rejected" },
];

export default function ContentPage() {
  const fmt = useLocaleFormat();
  const when = (value: string | null) =>
    value ? fmt.dateTime(value, "medium") : "—";
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [filter, setFilter] = useState<ReviewState | "all">("all");
  const [openSlug, setOpenSlug] = useState<string | null>(params.get("slug"));
  const [note, setNote] = useState("");
  const [refusal, setRefusal] = useState<string | null>(null);

  // The notification mail links to ?slug=…, so arriving from it opens that
  // post rather than dropping the reviewer into a list to search.
  useEffect(() => {
    const wanted = params.get("slug");
    if (wanted) setOpenSlug(wanted);
  }, [params]);

  const list = useQuery({
    queryKey: ["staff", "content", filter],
    queryFn: async () => {
      const query = filter === "all" ? "" : `?state=${filter}`;
      const res = await apiClient.get<{
        success: boolean;
        data: ListResponse;
        meta?: { notificationRecipients?: number };
      }>(`/staff/marketing/content${query}`);
      return res.data;
    },
  });

  /**
   * Proposals waiting to replace a live post, and the posts most worth
   * refreshing. Shown above the queue because a live article that is wrong
   * costs more than a draft that is not written yet.
   */
  const refresh = useQuery({
    queryKey: ["staff", "content", "refresh"],
    queryFn: async () => {
      const res = await apiClient.get<{
        success: boolean;
        data: { proposals: Revision[]; candidates: RefreshCandidate[] };
      }>("/staff/marketing/content/refresh");
      return res.data.data;
    },
  });

  const actOnRevision = useMutation({
    mutationFn: async (input: {
      slug: string;
      action: "approve" | "reject";
      note?: string;
    }) => {
      const res = await apiClient.post<{ success: boolean }>(
        `/staff/marketing/content/${input.slug}/revision/${input.action}`,
        { note: input.note },
      );
      return res.data;
    },
    onSuccess: () => {
      setRefusal(null);
      setNote("");
      void queryClient.invalidateQueries({ queryKey: ["staff", "content"] });
    },
    onError: (error: unknown) => {
      const message =
        (error as { response?: { data?: { error?: { message?: string } } } })
          ?.response?.data?.error?.message ?? "The revision could not be saved";
      setRefusal(message);
    },
  });

  const detail = useQuery({
    queryKey: ["staff", "content", "detail", openSlug],
    enabled: Boolean(openSlug),
    queryFn: async () => {
      const res = await apiClient.get<{
        success: boolean;
        data: ContentItem & { content: string };
      }>(`/staff/marketing/content/${openSlug}`);
      return res.data.data;
    },
  });

  const act = useMutation({
    mutationFn: async (input: {
      slug: string;
      action: "approve" | "reject" | "unpublish";
      note?: string;
    }) => {
      // The stored checks date from when the draft was written. Run them
      // again first, so a check corrected since does not refuse the approval
      // and one that fails now still does.
      if (input.action === "approve") {
        await apiClient.post(
          `/staff/marketing/content/${input.slug}/recheck`,
          {},
        );
      }
      const res = await apiClient.post<{
        success: boolean;
        error?: { message: string };
      }>(`/staff/marketing/content/${input.slug}/${input.action}`, {
        note: input.note,
      });
      return res.data;
    },
    onSuccess: (data) => {
      if (!data.success) {
        setRefusal(data.error?.message ?? "The action was refused");
        return;
      }
      setRefusal(null);
      setNote("");
      void queryClient.invalidateQueries({ queryKey: ["staff", "content"] });
    },
    onError: (error: unknown) => {
      // The server's reason, not "Request failed with status code 422": a
      // refusal names the check that failed, and that is what to act on.
      const message =
        (error as { response?: { data?: { error?: { message?: string } } } })
          ?.response?.data?.error?.message ??
        (error instanceof Error ? error.message : "The action failed");
      setRefusal(message);
    },
  });

  // Checks are stored when a draft is written, so a check that has been
  // corrected since still blocks the draft until they run again.
  const recheck = useMutation({
    mutationFn: async (slug: string) => {
      await apiClient.post(`/staff/marketing/content/${slug}/recheck`, {});
    },
    onSuccess: () => {
      setRefusal(null);
      void queryClient.invalidateQueries({ queryKey: ["staff", "content"] });
    },
    onError: () => setRefusal("The checks could not be run again"),
  });

  const items = list.data?.data.items ?? [];
  const counts = list.data?.data.counts ?? {};
  // `undefined` until a response actually arrives, and deliberately not
  // defaulted to 0: a failed request knows nothing about the server's mail
  // configuration, and the first version of this page announced
  // "notifications go nowhere" over a server where they worked.
  const recipients = list.data?.meta?.notificationRecipients;
  const post = detail.data;

  return (
    <div className="mx-auto max-w-6xl px-4 py-8">
      <header className="mb-6">
        <h1 className="flex items-center gap-2 text-2xl font-semibold text-gray-900">
          <FileText className="h-6 w-6 text-sky-600" aria-hidden="true" />
          Content
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-gray-600">
          Every blog entry. A draft is written by the content pipeline and stays
          unpublished until you approve it; your name is recorded on the post.
        </p>
      </header>

      {(refresh.data?.proposals.length ?? 0) > 0 && (
        <section className="mb-6 rounded-xl bg-sky-50 p-4 ring-1 ring-sky-600/20">
          <h2 className="text-sm font-semibold text-sky-900">
            Waiting to replace a live post
          </h2>
          <p className="mt-1 text-xs text-sky-800">
            The published version is still serving. Approving swaps this text
            over it; rejecting changes nothing a reader can see.
          </p>
          <ul className="mt-3 space-y-3">
            {refresh.data!.proposals.map((proposal) => (
              <li
                key={proposal.id}
                className="rounded-lg bg-white p-3 ring-1 ring-gray-200"
              >
                <p className="text-sm font-medium text-gray-900">
                  {proposal.title}
                </p>
                {/* The reason first: "CDC raised the risk level for Kenya" is a
                    different review from "no change in 11 months". */}
                <p className="mt-0.5 text-xs text-gray-600">
                  {proposal.reason}
                </p>
                {proposal.checks && (
                  <ul className="mt-2 flex flex-wrap gap-1.5">
                    {proposal.checks
                      .filter((item) => item.severity !== "pass")
                      .map((item) => (
                        <li
                          key={item.id}
                          className={`rounded-md px-2 py-1 text-xs ring-1 ring-inset ${CHECK_STYLE[item.severity]}`}
                        >
                          {item.id}: {item.detail}
                        </li>
                      ))}
                  </ul>
                )}
                <div className="mt-3 flex flex-wrap gap-2">
                  <a
                    className="rounded-md px-3 py-1.5 text-xs font-medium text-sky-800 ring-1 ring-sky-600/30"
                    href={`/blog/${proposal.slug}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Read the live version
                  </a>
                  <button
                    type="button"
                    className="rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
                    disabled={actOnRevision.isPending}
                    onClick={() =>
                      actOnRevision.mutate({
                        slug: proposal.slug,
                        action: "approve",
                        note: note || undefined,
                      })
                    }
                  >
                    Publish this version
                  </button>
                  <button
                    type="button"
                    className="rounded-md bg-white px-3 py-1.5 text-xs font-medium text-red-700 ring-1 ring-red-600/30 disabled:opacity-50"
                    disabled={actOnRevision.isPending || note.trim() === ""}
                    title={
                      note.trim() === ""
                        ? "Say what is wrong with it first"
                        : undefined
                    }
                    onClick={() =>
                      actOnRevision.mutate({
                        slug: proposal.slug,
                        action: "reject",
                        note,
                      })
                    }
                  >
                    Reject
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {(refresh.data?.candidates.length ?? 0) > 0 && (
        <details className="mb-6 rounded-xl bg-gray-50 p-4 ring-1 ring-gray-200">
          <summary className="cursor-pointer text-sm font-semibold text-gray-900">
            Most worth refreshing ({refresh.data!.candidates.length})
          </summary>
          <ul className="mt-3 space-y-1.5 text-xs text-gray-700">
            {refresh.data!.candidates.map((candidate) => (
              <li key={candidate.slug} className="flex flex-wrap gap-2">
                <span className="font-medium">{candidate.title}</span>
                <span className="text-gray-500">{candidate.reason}</span>
                <span className="text-gray-400">
                  {/* Never a score computed from a handful of readers: below
                      100 the API sends null and this says so. */}
                  {candidate.score === null
                    ? `score unavailable (${candidate.readers} readers)`
                    : `score ${candidate.score}`}
                </span>
              </li>
            ))}
          </ul>
        </details>
      )}

      {list.isSuccess && recipients === 0 && (
        <div
          className="mb-6 flex items-start gap-3 rounded-lg bg-amber-50 p-4 text-sm text-amber-900 ring-1 ring-amber-600/20"
          role="status"
        >
          <MailWarning className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
          <div>
            <p className="font-medium">Review notifications go nowhere.</p>
            <p className="mt-1">
              <code>CONTENT_REVIEW_EMAILS</code> is not set on the server, so a
              new draft will sit here silently. This page is then the only place
              it appears.
            </p>
          </div>
        </div>
      )}

      <div className="mb-4 flex flex-wrap gap-2" role="tablist">
        {FILTERS.map((entry) => {
          const active = filter === entry.id;
          const count =
            entry.id === "all"
              ? undefined
              : (counts[entry.id as ReviewState] ?? 0);
          return (
            <button
              key={entry.id}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setFilter(entry.id)}
              className={`rounded-full px-3 py-1.5 text-sm ring-1 ${
                active
                  ? "bg-sky-600 text-white ring-sky-600"
                  : "bg-white text-gray-700 ring-gray-300 hover:bg-gray-50"
              }`}
            >
              {entry.label}
              {count !== undefined && count > 0 && (
                <span className="ml-1.5 opacity-75">{count}</span>
              )}
            </button>
          );
        })}
      </div>

      {refusal && (
        <div
          className="mb-4 flex items-start gap-3 rounded-lg bg-red-50 p-4 text-sm text-red-900 ring-1 ring-red-600/20"
          role="alert"
        >
          <AlertTriangle
            className="mt-0.5 h-5 w-5 shrink-0"
            aria-hidden="true"
          />
          <p>{refusal}</p>
        </div>
      )}

      {list.isLoading ? (
        <p className="flex items-center gap-2 text-sm text-gray-600">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Loading content…
        </p>
      ) : list.isError ? (
        <p className="text-sm text-red-700">
          The content list could not be loaded.
        </p>
      ) : items.length === 0 ? (
        <p className="rounded-lg bg-gray-50 p-6 text-sm text-gray-600">
          Nothing here yet. The pipeline writes two drafts a week (MAR-003).
        </p>
      ) : (
        <ul className="space-y-3">
          {items.map((item) => {
            const open = openSlug === item.slug;
            return (
              <li
                key={item.id}
                className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${STATE_STYLE[item.reviewState]}`}
                      >
                        {STATE_LABEL[item.reviewState]}
                      </span>
                      {item.aiGenerated && (
                        <span className="flex items-center gap-1 text-xs text-gray-500">
                          <Sparkles className="h-3 w-3" aria-hidden="true" />
                          AI-drafted
                        </span>
                      )}
                      {item.published && (
                        <a
                          href={`/blog/${item.slug}`}
                          target="_blank"
                          rel="noreferrer"
                          className="flex items-center gap-1 text-xs text-sky-700 hover:underline"
                        >
                          <ExternalLink
                            className="h-3 w-3"
                            aria-hidden="true"
                          />
                          Live
                        </a>
                      )}
                    </div>
                    <h2 className="mt-1.5 truncate text-base font-medium text-gray-900">
                      {item.title}
                    </h2>
                    <p className="mt-0.5 line-clamp-2 text-sm text-gray-600">
                      {item.excerpt}
                    </p>
                    <p className="mt-1.5 text-xs text-gray-500">
                      {item.category}
                      {item.primaryKeyword && ` · ${item.primaryKeyword}`}
                      {item.readTimeMin && ` · ${item.readTimeMin} min read`}
                      {" · written "}
                      {when(item.createdAt)}
                    </p>
                    {item.reviewedBy && (
                      <p className="mt-1 flex items-center gap-1 text-xs text-gray-600">
                        <CheckCircle2
                          className="h-3 w-3 text-emerald-600"
                          aria-hidden="true"
                        />
                        {item.reviewState === "rejected"
                          ? "Rejected by "
                          : "Reviewed by "}
                        {item.reviewedBy} on {when(item.reviewedAt)}
                      </p>
                    )}
                    {item.reviewNote && (
                      <p className="mt-1 text-xs italic text-gray-600">
                        “{item.reviewNote}”
                      </p>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      const next = open ? null : item.slug;
                      setOpenSlug(next);
                      setNote("");
                      setRefusal(null);
                      // Keep the URL shareable: the mail links here, and so
                      // should a link pasted to somebody else.
                      if (next) setParams({ slug: next });
                      else setParams({});
                    }}
                    className="shrink-0 rounded-md bg-gray-900 px-3 py-1.5 text-sm text-white hover:bg-gray-700"
                  >
                    {open ? "Close" : "Read it"}
                  </button>
                </div>

                {open && (
                  <div className="mt-4 border-t border-gray-200 pt-4">
                    {detail.isLoading ? (
                      <p className="flex items-center gap-2 text-sm text-gray-600">
                        <Loader2
                          className="h-4 w-4 animate-spin"
                          aria-hidden="true"
                        />
                        Loading the post…
                      </p>
                    ) : !post ? (
                      <p className="text-sm text-red-700">
                        The post could not be loaded.
                      </p>
                    ) : (
                      <>
                        <dl className="mb-4 grid gap-x-6 gap-y-1 text-xs text-gray-600 sm:grid-cols-2">
                          <div className="flex gap-2">
                            <dt className="font-medium">Slug</dt>
                            <dd className="truncate">/blog/{post.slug}</dd>
                          </div>
                          <div className="flex gap-2">
                            <dt className="font-medium">Meta title</dt>
                            <dd className="truncate">
                              {post.metaTitle ?? "—"}
                            </dd>
                          </div>
                          <div className="flex gap-2 sm:col-span-2">
                            <dt className="font-medium">Meta description</dt>
                            <dd>{post.metaDesc ?? "—"}</dd>
                          </div>
                        </dl>

                        {post.checks === null ? (
                          <p className="mb-4 flex items-center gap-2 rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-700 ring-1 ring-gray-300">
                            <AlertTriangle className="h-4 w-4 shrink-0" />
                            {/* Not the same as passing: a blank here would be
                                indistinguishable from a clean bill of health. */}
                            The pre-flight checks have never run on this draft.
                          </p>
                        ) : (
                          <ul className="mb-4 flex flex-wrap gap-1.5">
                            {post.checks.map((item) => (
                              <li
                                key={item.id}
                                className={`rounded-md px-2 py-1 text-xs ring-1 ring-inset ${CHECK_STYLE[item.severity]}`}
                                title={item.detail}
                              >
                                <span className="font-medium">{item.id}</span>{" "}
                                <span className="opacity-80">
                                  {item.detail}
                                </span>
                              </li>
                            ))}
                            {!post.published && (
                              <li>
                                <button
                                  type="button"
                                  className="rounded-md px-2 py-1 text-xs text-gray-700 ring-1 ring-gray-300 hover:bg-gray-50 disabled:opacity-50"
                                  disabled={recheck.isPending}
                                  onClick={() => recheck.mutate(post.slug)}
                                >
                                  {recheck.isPending
                                    ? "Checking…"
                                    : "Run checks again"}
                                </button>
                              </li>
                            )}
                          </ul>
                        )}

                        {post.imageUrl ? (
                          <figure className="mb-4">
                            <img
                              src={post.imageUrl}
                              alt={post.imageAlt ?? ""}
                              className="aspect-[3/2] w-full rounded-lg object-cover"
                            />
                            {/* Shown, not hidden in an attribute: the alt text
                                is part of what is being approved, and nobody
                                reviews what the page does not display. */}
                            <figcaption className="mt-1 text-xs text-gray-500">
                              Alt text: {post.imageAlt || "— missing —"}
                            </figcaption>
                          </figure>
                        ) : (
                          <p className="mb-4 flex items-center gap-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800 ring-1 ring-amber-600/20">
                            <AlertTriangle className="h-4 w-4 shrink-0" />
                            No illustration yet — this post cannot be published
                            until one is generated.
                          </p>
                        )}

                        <div
                          className="prose prose-sm prose-headings:text-gray-900 prose-a:text-blue-700 max-w-none rounded-lg bg-white p-5 text-gray-800 ring-1 ring-gray-200"
                          // HTML by design (MAR-002), but written by a model,
                          // and shown to a signed-in staff member whose session
                          // is worth more than a reader's — so sanitised here
                          // as well as on save.
                          dangerouslySetInnerHTML={{
                            __html: sanitizeArticleHtml(post.content),
                          }}
                        />

                        <div className="mt-4 space-y-3">
                          <label
                            className="block text-sm font-medium text-gray-800"
                            htmlFor={`note-${post.slug}`}
                          >
                            Note
                            <span className="ml-1 font-normal text-gray-500">
                              (required to reject — say what is wrong)
                            </span>
                          </label>
                          <textarea
                            id={`note-${post.slug}`}
                            value={note}
                            onChange={(event) => setNote(event.target.value)}
                            rows={2}
                            className="w-full rounded-md border border-gray-300 p-2 text-sm"
                            placeholder="Optional on approval; kept with the post either way."
                          />

                          <div className="flex flex-wrap gap-2">
                            <button
                              type="button"
                              disabled={act.isPending}
                              onClick={() =>
                                act.mutate({
                                  slug: post.slug,
                                  action: "approve",
                                  note: note.trim() || undefined,
                                })
                              }
                              className="flex items-center gap-2 rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                            >
                              <CheckCircle2
                                className="h-4 w-4"
                                aria-hidden="true"
                              />
                              Approve and publish
                            </button>
                            <button
                              type="button"
                              disabled={act.isPending || !note.trim()}
                              onClick={() =>
                                act.mutate({
                                  slug: post.slug,
                                  action: "reject",
                                  note: note.trim(),
                                })
                              }
                              className="flex items-center gap-2 rounded-md bg-white px-4 py-2 text-sm font-medium text-red-700 ring-1 ring-red-300 hover:bg-red-50 disabled:opacity-50"
                            >
                              <XCircle className="h-4 w-4" aria-hidden="true" />
                              Reject
                            </button>
                            {post.published && (
                              <button
                                type="button"
                                disabled={act.isPending}
                                onClick={() =>
                                  act.mutate({
                                    slug: post.slug,
                                    action: "unpublish",
                                    note: note.trim() || undefined,
                                  })
                                }
                                className="flex items-center gap-2 rounded-md bg-white px-4 py-2 text-sm font-medium text-gray-700 ring-1 ring-gray-300 hover:bg-gray-50 disabled:opacity-50"
                              >
                                <EyeOff
                                  className="h-4 w-4"
                                  aria-hidden="true"
                                />
                                Take it down
                              </button>
                            )}
                            {act.isPending && (
                              <span className="flex items-center gap-2 text-sm text-gray-600">
                                <Loader2
                                  className="h-4 w-4 animate-spin"
                                  aria-hidden="true"
                                />
                                Saving…
                              </span>
                            )}
                          </div>

                          <p className="flex items-start gap-1.5 text-xs text-gray-500">
                            <Clock
                              className="mt-0.5 h-3 w-3 shrink-0"
                              aria-hidden="true"
                            />
                            Approving records your name and the time against
                            this post, and publishes it immediately.
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
